import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { NAMES_DATABASE, NameData } from '../data/names';
import AdArea from './AdArea';

interface NameGeneratorModalProps {
  show: boolean;
  onHide: () => void;
  onGenerate: (names: string) => void;
}

const CATEGORIES: { value: NameData['category']; label: string }[] = [
  { value: 'feminine', label: 'Femininos' },
  { value: 'masculine', label: 'Masculinos' },
  { value: 'neutral', label: 'Neutros' },
  { value: 'toys', label: 'Brinquedos' },
  { value: 'utilities', label: 'Utilitários' },
  { value: 'clothes', label: 'Roupas' },
];

const NameGeneratorModal: React.FC<NameGeneratorModalProps> = ({ show, onHide, onGenerate }) => {
  const [selectedCategories, setSelectedCategories] = useState<NameData['category'][]>(CATEGORIES.map(c => c.value));
  const [count, setCount] = useState<number>(25);

  const handleToggleCategory = (category: NameData['category']) => {
    console.log("[DEBUG_LOG] NameGeneratorModal.handleToggleCategory - Toggling category:", category);
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category) 
        : [...prev, category]
    );
  };

  const handleToggleAll = () => {
    console.log("[DEBUG_LOG] NameGeneratorModal.handleToggleAll - Toggling all categories");
    if (selectedCategories.length === CATEGORIES.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(CATEGORIES.map(c => c.value));
    }
  };

  const handleGenerate = () => {
    console.log("[DEBUG_LOG] NameGeneratorModal.handleGenerate - Generating names. Categories:", selectedCategories, "Count:", count);
    
    let effectiveCount = count;
    const maxLimit = selectedCategories.length * 50;
    
    if (count > maxLimit) {
      effectiveCount = maxLimit;
      console.log("[DEBUG_LOG] NameGeneratorModal.handleGenerate - Count exceeds dynamic limit, limiting to", effectiveCount);
    }

    const filteredNames = NAMES_DATABASE.filter(n => selectedCategories.includes(n.category));

    if (filteredNames.length === 0) {
      console.log("[DEBUG_LOG] NameGeneratorModal.handleGenerate - No names found for selected categories");
      alert("Selecione pelo menos uma categoria.");
      return;
    }

    const shuffled = [...filteredNames].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, Math.min(effectiveCount, shuffled.length));
    const resultString = selected.map(n => n.name).join(', ');

    onGenerate(resultString);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered style={{ color: 'var(--bingo-text)' }}>
      <Modal.Header closeButton style={{ backgroundColor: 'var(--bingo-card-bg)', borderBottomColor: 'var(--bingo-border)' }}>
        <Modal.Title style={{ color: 'var(--bingo-text)' }}>Gerar Nomes</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: 'var(--bingo-card-bg)' }}>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold" style={{ color: 'var(--bingo-text)' }}>Categorias</Form.Label>
          <div className="mb-2">
            <Form.Check 
              type="checkbox"
              id="category-all"
              label="Selecionar Todas"
              checked={selectedCategories.length === CATEGORIES.length}
              onChange={handleToggleAll}
            />
          </div>
          <hr />
          <div className="d-flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <Form.Check 
                key={cat.value}
                type="checkbox"
                id={`category-${cat.value}`}
                label={cat.label}
                className="me-3"
                checked={selectedCategories.includes(cat.value)}
                onChange={() => handleToggleCategory(cat.value)}
              />
            ))}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Número de nomes a gerar</Form.Label>
          <Form.Control 
            type="number" 
            value={count} 
            onChange={(e) => {
              const val = parseInt(e.target.value);
              console.log("[DEBUG_LOG] NameGeneratorModal - Count changed to", val);
              setCount(val);
            }} 
            min={1}
            max={selectedCategories.length * 50}
          />
          {selectedCategories.length > 0 && (
            <Form.Text style={{ color: 'var(--bingo-text-muted)' }}>
              Sugestão: no mínimo 24 nomes. Limite de {selectedCategories.length * 50} nesta seleção.
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <AdArea />
      <Modal.Footer style={{ backgroundColor: 'var(--bingo-card-bg)', borderTopColor: 'var(--bingo-border)' }}>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGenerate} disabled={selectedCategories.length === 0}>
          Gerar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NameGeneratorModal;
